import { ActivatedRouteSnapshot, Data, Routes } from '@angular/router';
import { EditionGuard } from './core/utils/edition.guard';
import { ASSETS_SUBMENUS, ITSM_SUBMENUS, IMS_SUBMENUS } from './core/data/submenu.data';
import { resourceTypeGuard } from './core/utils/resource-type.guard';
import { resourceDetailGuard } from './core/utils/resource-detail.guard';
import { resourceTopicGuard } from './core/utils/resource-topic.guard';

const PRODUCT_CHILD_ROUTES: Routes = [
  {
    path: 'it-asset-management',
    data: { breadcrumb: 'IT Asset Management Software' },
    loadChildren: () =>
      import('./products/infraon-assets/infraon-assets.routes').then(
        (m) => m.INFRAON_ASSETS_ROUTES,
      ),
  },
  {
    path: 'itsm-tool',
    data: { breadcrumb: 'ITSM Tool' },
    loadChildren: () =>
      import('./products/infraon-itsm/infraon-itsm.routes').then((m) => m.INFRAON_ITSM_ROUTES),
  },
];

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./layout/global/global').then((m) => m.Global),
    children: [
      // homepage
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./pages/homepage/homepage').then((m) => m.SetContext),
        data: {
          meta_title: 'Unified IT Operations Management Platform | Replace Tool Sprawl',
          meta_desc:
            'Unified ITOps platform for ITAM, ITSM, and IMS. Replace tool sprawl with governed ITOps architecture in 300+ deployments.',
          canonical_url: '/',
          colorScheme: 'infinity-theme',
          og_url: 'https://infraon.io/',
          og_image: 'https://infraon.io/assets/img/feature-imgs/homepage/hero-dashboard.png',
          og_type: 'website',
          og_title: 'Unified IT Operations Management Platform | Replace Tool Sprawl',
          og_desc:
            'Unified ITOps platform for ITAM, ITSM, and IMS. Replace tool sprawl with governed ITOps architecture in 300+ deployments.',
          site_name: 'Infraon',
          og_image_type: 'image/png',
          og_image_alt:
            'Infraon IT Operations Management dashboard showing unified view of assets, services, and network performance',

          json_ld: {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Infraon',
            url: 'https://infraon.io/',
          },
          breadcrumb: null,
          breadcrumb_color: null,
        },
      },

      //sass assets page
      {
        path: 'it-asset-management',
        pathMatch: 'full',
        canActivate: [EditionGuard],
        loadComponent: () =>
          import('./products/infraon-assets/infraon-assets-switcher').then(
            (m) => m.InfraonAssetsSwitcher,
          ),
        data: {
          variant: 'saas',
          hasSubmenu: true,
          hasDualContext: true,
          breadcrumb: 'IT Assets Management Software',
          breadcrumb_color: 'IT Assets Management',
          colorScheme: 'assets-theme',
          sitemapKey: 'Asset',
          productName: 'Infraon Assets',
          pricing_url: '/it-asset-management/prices/assets-pricing',
          hasPricing: true,
          productIcon: '/assets/img/logos/products/assets-element.svg',
          meta_title: 'IT Asset Management Software | Infraon ITAM Tool',
          meta_desc: `Cut IT costs and stay audit-ready with Infraon's IT asset management software - a smart ITAM tool that automates asset tracking, licensing, and compliance.`,
          canonical_url: '/it-asset-management',

          og_title: 'IT Asset Management Software | Infraon ITAM Tool',
          og_url: 'https://infraon.io/it-asset-management',
          og_desc: `Cut IT costs and stay audit-ready with Infraon's IT asset management software - a smart ITAM tool that automates asset tracking, licensing, and compliance.`,
          og_image: 'https://infraon.io/assets/img/feature-imgs/homepage/hero-dashboard.png',
          og_image_type: 'image/png',
          og_image_alt:
            'Infraon IT Asset Management dashboard showing asset inventory and lifecycle tracking',

          twitter_title: 'IT Asset Management Software | Infraon ITAM Tool',
          twitter_desc: `Cut IT costs and stay audit-ready with Infraon's IT asset management software - a smart ITAM tool that automates asset tracking, licensing, and compliance.`,
          twitter_image: 'https://infraon.io/assets/img/feature-imgs/homepage/hero-dashboard.png',
          twitter_image_alt:
            'Infraon IT Asset Management dashboard showing asset inventory and lifecycle tracking',
          submenu: ASSETS_SUBMENUS,
          footerBanner: {
            bgImage: '/assets/img/bgs/assets/cta-banner.svg',
            titleHtml: `<span class="emphasis">Why Run Asset Tracking in Fragments?</span>Move to One Module!`,
            descHtml: ``,
            pointers: ['Free Trail Available', 'Custom Demo', 'Live ROI Analysis'],
            color: 'assets',
            cta: 'Start Your Free Trial',
            link: 'it-asset-management',
          },
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./products/infraon-assets/sass/sass-infraon-assets').then(
                (m) => m.SassInfraonAssets,
              ),
            data: {
              colorScheme: 'assets-theme',
            },
          },
        ],
      },
      {
        path: 'enterprise',
        data: {
          breadcrumb: 'Enterprise',
          sitemapKey: 'enterprise',
          canonical_url: '/enterprise',

          meta_title: 'Enterprise ITSM, ITAM & IT Operations Software | Infraon',
          meta_desc: `Explore Infraon's enterprise-grade IT operations, asset management, and service management software — built for large, global organizations at scale.`,

          og_title: 'Enterprise ITSM, ITAM & IT Operations Software | Infraon',
          og_url: 'https://infraon.io/enterprise',
          og_desc: `Explore Infraon's enterprise-grade IT operations, asset management, and service management software — built for large, global organizations at scale.`,
          og_image: 'https://infraon.io/assets/img/feature-imgs/homepage/hero-dashboard.png',
          og_image_type: 'image/png',
          og_image_alt: 'Infraon enterprise IT operations dashboard for global organizations',

          twitter_title: 'Enterprise ITSM, ITAM & IT Operations Software | Infraon',
          twitter_desc: `Explore Infraon's enterprise-grade IT operations, asset management, and service management software — built for large, global organizations at scale.`,
          twitter_image: 'https://infraon.io/assets/img/feature-imgs/homepage/hero-dashboard.png',
          twitter_image_alt: 'Infraon enterprise IT operations dashboard for global organizations',
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/enterprise-list/enterprise-list').then(
                (m) => m.EnterpriseListComponent,
              ),
          },
          // assets
          {
            path: 'it-asset-management',
            pathMatch: 'full',
            canActivate: [EditionGuard],
            loadComponent: () =>
              import('./products/infraon-assets/infraon-assets-switcher').then(
                (m) => m.InfraonAssetsSwitcher,
              ),
            data: {
              variant: 'enterprise',
              breadcrumb: 'IT Asset Management Software',
              breadcrumb_color: 'Infraon Asset Management Software',
              hasSubmenu: true,
              hasDualContext: true,
              sitemapKey: 'Asset',
              productName: 'Infraon Assets',
              hasPricing: false,
              productIcon: '/assets/img/logos/products/assets-element.svg',
              colorScheme: 'assets-theme',
              meta_title: 'Enterprise Asset Management Software | Infraon EAM',
              meta_desc: `Manage the full asset lifecycle with Infraon's enterprise asset management software - AI-driven EAM software that automates compliance and cuts downtime.`,
              canonical_url: '/enterprise/it-asset-management',

              og_title: 'Enterprise Asset Management Software | Infraon EAM',
              og_url: 'https://infraon.io/enterprise/it-asset-management',
              og_desc: `Manage the full asset lifecycle with Infraon's enterprise asset management software - AI-driven EAM software that automates compliance and cuts downtime.`,
              og_image: 'https://infraon.io/assets/img/feature-imgs/assets/asset-dashboard.png',
              og_image_type: 'image/png',
              og_image_alt:
                'Infraon Enterprise Asset Management dashboard showing asset lifecycle and compliance status',

              twitter_title:
                'Enterprise Asset Management Software | EAM for Global Organizations | Infraon',
              twitter_desc: `Manage the full asset lifecycle with Infraon's enterprise asset management software - AI-driven EAM software that automates compliance and cuts downtime.`,
              twitter_image:
                'https://infraon.io/assets/img/feature-imgs/assets/asset-dashboard.png',
              twitter_image_alt:
                'Infraon Enterprise Asset Management dashboard showing asset lifecycle and compliance status',

              submenu: ASSETS_SUBMENUS,
              footerBanner: {
                bgImage: '/assets/img/bgs/assets/cta-banner.svg',
                titleHtml: `<span class="emphasis"><em>Trusted</em> by 300+ Global Enterprises</span>`,
                descHtml: `Organizations including Terrabit, IDfy, and Healthium have selected Infraon Assets to optimize asset expenditure and automate lifecycle management.`,
                pointers: [
                  'Enterprise evaluation program available',
                  'Custom ROI analysis included',
                  'Dedicated implementation consultation',
                ],
                color: 'assets',
                cta: 'Book Your Demo',
              },
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./products/infraon-assets/enterprise/infraon-assets').then(
                    (m) => m.InfraonAssets,
                  ),
              },
            ],
          },

          // itsm
          {
            path: 'itsm-tool',
            pathMatch: 'full',
            canActivate: [EditionGuard],
            loadComponent: () =>
              import('./products/infraon-itsm/infraon-itsm-switcher').then(
                (m) => m.InfraonItsmSwitcher,
              ),
            data: {
              variant: 'enterprise',
              hasSubmenu: true,
              hasDualContext: true,
              breadcrumb: 'ITSM Tool',
              breadcrumb_color: 'Infraon ITSM Software',
              sitemapKey: 'ITSM',
              productName: 'Infraon ITSM',
              productIcon: '/assets/img/logos/products/itsm-element.svg',
              hasPricing: false,
              meta_title: 'Enterprise IT Management Software | IT Service Management',
              meta_desc: `Infraon's enterprise IT management software delivers GenAI-powered IT service management - automating incidents, changes, and requests for faster resolutions.`,
              canonical_url: '/enterprise/itsm-tool',
              colorScheme: 'itsm-theme',

              twitter_title: 'Enterprise IT Management Software | IT Service Management',
              twitter_desc: `Infraon's enterprise IT management software delivers GenAI-powered IT service management - automating incidents, changes, and requests for faster resolutions.`,
              twitter_image: 'https://infraon.io/assets/img/feature-imgs/itsm/itsm-dashboard.png',
              twitter_image_alt:
                'Infraon Enterprise ITSM dashboard with incident management and SLA tracking',

              og_image:
                'https://infraon.io/assets/img/feature-img/itsm-incident-management-feature-5-01.png',
              og_type: 'website',
              og_image_type: 'image/png',
              og_image_alt:
                'Infraon Enterprise ITSM dashboard with incident management and SLA tracking',

              og_url: 'https://infraon.io/enterprise/itsm-tool',
              site_name: 'Infraon',
              json_ld: {
                '@context': 'https://schema.org',
                '@type': 'WebSite',
                name: 'Infraon',
                url: 'https://infraon.io/',
              },
              submenu: ITSM_SUBMENUS,
              footerBanner: {
                bgImage: '/assets/img/bgs/assets/cta-banner.svg',
                titleHtml: `<span class="emphasis"><em>Trusted</em> by Global Organizations for Service Excellence</span>`,
                descHtml: `From global restaurant chains to leading insurers organizations eliminate service management inefficiencies, maintain 99.9% uptime, and achieve 30% operational cost reduction with Infraon GenAI-powered IT service management.`,
                pointers: [
                  'Enterprise evaluation program available',
                  'Custom platform walkthrough included',
                  'ROI analysis and business case support',
                ],
                color: 'itsm',
                cta: 'Book a Quick Demo',
              },
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./products/infraon-itsm/enterprise/infraon-itsm').then(
                    (m) => m.InfraonItsm,
                  ),
              },
            ],
          },

          // itops
          {
            path: 'it-ops-management',
            canActivate: [EditionGuard],
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () =>
                  import('./products/infraon-itops/enterprise/homepage').then((m) => m.Homepage),
                data: {
                  variant: 'enterprise',
                  hasSubmenu: false,
                  hasDualContext: true,
                  meta_title: 'Unified IT Operations Management | Infraon',
                  meta_desc:
                    'Infraon Infinity unifies ITOps across service, assets, and infrastructure with GenAI-powered automation and real-time visibility for enterprise IT teams.',
                  canonical_url: '/enterprise/it-ops-management',

                  colorScheme: 'infinity-theme',

                  og_url: 'https://infraon.io/enterprise/it-ops-management',
                  og_image: 'https://infraon.io/assets/img/feature-imgs/nms/ims-dashboard.png',
                  og_type: 'website',
                  site_name: 'Infraon',
                  og_image_type: 'image/png',
                  og_image_alt:
                    'Infraon Infinity enterprise IT operations management unified dashboard',

                  twitter_title: 'Unified IT Operations Management | Infraon Infinity Enterprise',
                  twitter_desc:
                    'Infraon Infinity unifies ITOps across service, assets, and infrastructure with GenAI-powered automation and real-time visibility for enterprise IT teams.',
                  twitter_image: 'https://infraon.io/assets/img/feature-imgs/nms/ims-dashboard.png',
                  twitter_image_alt:
                    'Infraon Infinity enterprise IT operations management unified dashboard',

                  json_ld: {
                    '@context': 'https://schema.org',
                    '@type': 'WebSite',
                    name: 'Infraon',
                    url: 'https://infraon.io/',
                  },

                  breadcrumb: 'IT Operations',
                  breadcrumb_color: 'white',
                  footerBanner: {
                    bgImage: '/assets/img/feature-imgs/homepage/footer-cta.svg',
                    titleHtml: `<span class="emphasis">You Never Have To Look for Another IT Operations Management Platform</span>`,
                    descHtml: ``,
                    pointers: [
                      'No credit card details required',
                      'Custom walkthrough for your use case',
                      'Live savings calculator',
                    ],
                    color: 'infinity',
                    cta: 'Take Your Free Trial',
                  },
                },
              },

              // persona-based landing pages
              {
                path: 'asset-head-process-manager',
                loadComponent: () =>
                  import('./products/infraon-itops/enterprise/personas/asset-head-process-manager/asset-head-process-manager').then(
                    (m) => m.AssetHeadProcessManager,
                  ),
                data: {
                  variant: 'enterprise',
                  hasSubmenu: false,
                  hasDualContext: true,
                  meta_title: 'IT Asset Management Software for Asset & Process Heads',
                  meta_desc:
                    'Simplify IT asset and process management with Infraon. Gain asset visibility, automate workflows, control costs and improve operational efficiency.',
                  canonical_url: '/enterprise/it-ops-management/asset-head-process-manager',
                  colorScheme: 'infinity-theme',
                  og_url:
                    'https://infraon.io/enterprise/it-ops-management/asset-head-process-manager',
                  og_type: 'website',
                  site_name: 'Infraon',
                  og_title: 'IT Asset Management Software for Asset & Process Heads',
                  og_desc:
                    'Simplify IT asset and process management with Infraon. Gain asset visibility, automate workflows, control costs and improve operational efficiency.',
                  twitter_title: 'IT Asset Management Software for Asset & Process Heads',
                  twitter_desc:
                    'Simplify IT asset and process management with Infraon. Gain asset visibility, automate workflows, control costs and improve operational efficiency.',
                  og_image:
                    'https://infraon.io/assets/img/feature-pages/persona-based/asset-heads/asset-heads-hero.png',
                  og_image_type: 'image/png',
                  og_image_alt: 'Single CMDB auto-discovery dashboard preview',
                  twitter_image:
                    'https://infraon.io/assets/img/feature-pages/persona-based/asset-heads/asset-heads-hero.png',
                  twitter_image_alt: 'Single CMDB auto-discovery dashboard preview',
                  breadcrumb: 'Asset Heads & Process Managers',
                },
              },
              {
                path: 'noc-service-teams',
                loadComponent: () =>
                  import('./products/infraon-itops/enterprise/personas/noc-service-teams/noc-service-teams').then(
                    (m) => m.NocServiceTeams,
                  ),
                data: {
                  variant: 'enterprise',
                  hasSubmenu: false,
                  hasDualContext: true,
                  meta_title: 'IT Operations Software for NOC & Service Teams | Infraon',
                  meta_desc:
                    'Streamline NOC and service operations with unified ITSM, ITAM and IT operations software for faster incident resolution, monitoring and service delivery.',
                  canonical_url: '/enterprise/it-ops-management/noc-service-teams',
                  colorScheme: 'infinity-theme',
                  og_url: 'https://infraon.io/enterprise/it-ops-management/noc-service-teams',
                  og_type: 'website',
                  site_name: 'Infraon',
                  og_title: 'IT Operations Software for NOC & Service Teams | Infraon',
                  og_desc:
                    'Streamline NOC and service operations with unified ITSM, ITAM and IT operations software for faster incident resolution, monitoring and service delivery.',
                  twitter_title: 'IT Operations Software for NOC & Service Teams | Infraon',
                  twitter_desc:
                    'Streamline NOC and service operations with unified ITSM, ITAM and IT operations software for faster incident resolution, monitoring and service delivery.',
                  og_image:
                    'https://infraon.io/assets/img/feature-pages/persona-based/noc-service/noc-service-hero.png',
                  og_image_type: 'image/png',
                  og_image_alt: 'Infraon Infinity NOC event correlation dashboard',
                  twitter_image:
                    'https://infraon.io/assets/img/feature-pages/persona-based/noc-service/noc-service-hero.png',
                  twitter_image_alt: 'Infraon Infinity NOC event correlation dashboard',
                  breadcrumb: 'NOC & Service Teams',
                },
              },
              {
                path: 'it-director-it-manager',
                loadComponent: () =>
                  import('./products/infraon-itops/enterprise/personas/it-director-it-manager/it-director-it-manager').then(
                    (m) => m.ItDirectorItManager,
                  ),
                data: {
                  variant: 'enterprise',
                  hasSubmenu: false,
                  hasDualContext: true,
                  meta_title: 'IT Operations Software for IT Directors & Managers | Infraon',
                  meta_desc:
                    'Help IT Directors and Managers simplify IT operations with unified ITSM, ITAM and IT operations software for better control, automation and visibility.',
                  canonical_url: '/enterprise/it-ops-management/it-director-it-manager',
                  colorScheme: 'infinity-theme',
                  og_url: 'https://infraon.io/enterprise/it-ops-management/it-director-it-manager',
                  og_type: 'website',
                  site_name: 'Infraon',
                  og_title: 'IT Operations Software for IT Directors & Managers | Infraon',
                  og_desc:
                    'Help IT Directors and Managers simplify IT operations with unified ITSM, ITAM and IT operations software for better control, automation and visibility.',
                  twitter_title: 'IT Operations Software for IT Directors & Managers | Infraon',
                  twitter_desc:
                    'Help IT Directors and Managers simplify IT operations with unified ITSM, ITAM and IT operations software for better control, automation and visibility.',
                  og_image:
                    'https://infraon.io/assets/img/feature-pages/persona-based/it-directors/it-directors-hero.png',
                  og_image_type: 'image/png',
                  og_image_alt: 'Infraon Infinity IT operations health and delivery dashboard',
                  twitter_image:
                    'https://infraon.io/assets/img/feature-pages/persona-based/it-directors/it-directors-hero.png',
                  twitter_image_alt: 'Infraon Infinity IT operations health and delivery dashboard',
                  breadcrumb: 'IT Directors & IT Managers',
                },
              },
              {
                path: 'cio-cfo-cto',
                loadComponent: () =>
                  import('./products/infraon-itops/enterprise/personas/cio-cfo-cto/cio-cfo-cto').then(
                    (m) => m.CioCfoCto,
                  ),
                data: {
                  variant: 'enterprise',
                  hasSubmenu: false,
                  hasDualContext: true,
                  meta_title: 'IT Operations Software for CIOs, CFOs & CTOs | Infraon',
                  meta_desc:
                    'Empower CIOs, CFOs and CTOs with unified ITSM, ITAM and IT operations software to improve visibility, control costs and drive IT efficiency.',
                  canonical_url: '/enterprise/it-ops-management/cio-cfo-cto',
                  colorScheme: 'infinity-theme',
                  og_url: 'https://infraon.io/enterprise/it-ops-management/cio-cfo-cto',
                  og_type: 'website',
                  site_name: 'Infraon',
                  og_title: 'IT Operations Software for CIOs, CFOs & CTOs | Infraon',
                  og_desc:
                    'Empower CIOs, CFOs and CTOs with unified ITSM, ITAM and IT operations software to improve visibility, control costs and drive IT efficiency.',
                  twitter_title: 'IT Operations Software for CIOs, CFOs & CTOs | Infraon',
                  twitter_desc:
                    'Empower CIOs, CFOs and CTOs with unified ITSM, ITAM and IT operations software to improve visibility, control costs and drive IT efficiency.',
                  og_image:
                    'https://infraon.io/assets/img/feature-pages/persona-based/cio/cio-cfo-cto-hero.png',
                  og_image_type: 'image/png',
                  og_image_alt: 'Infraon Infinity executive IT spend and ROI dashboard',
                  twitter_image:
                    'https://infraon.io/assets/img/feature-pages/persona-based/cio/cio-cfo-cto-hero.png',
                  twitter_image_alt: 'Infraon Infinity executive IT spend and ROI dashboard',
                  breadcrumb: 'CIO · CFO · CTO',
                },
              },
            ],
          },
        ],
      },

      //sass itsm page
      {
        path: 'itsm-tool',
        pathMatch: 'full',
        canActivate: [EditionGuard],
        loadComponent: () =>
          import('./products/infraon-itsm/infraon-itsm-switcher').then(
            (m) => m.InfraonItsmSwitcher,
          ),
        data: {
          variant: 'saas',
          hasSubmenu: true,
          hasDualContext: true,
          breadcrumb: 'ITSM Tool',
          breadcrumb_color: 'Infraon ITSM Software',
          sitemapKey: 'ITSM',
          productName: 'Infraon ITSM',
          productIcon: '/assets/img/logos/products/itsm-element.svg',
          pricing_url: '/itsm-tool/prices/itsm-pricing',
          hasPricing: true,
          canonical_url: '/itsm-tool',
          meta_title: 'ITSM Tool | AI-Enabled ITSM Software by Infraon',
          meta_desc: `Infraon's ITSM software auto-routes tickets, builds knowledge bases, and enforces SLA escalations - an AI-powered ITSM tool that speeds up IT service delivery.`,
          colorScheme: 'itsm-theme',
          og_title: 'ITSM Tool | AI-Enabled ITSM Software by Infraon',
          og_url: 'https://infraon.io/itsm-tool',
          og_desc: `Infraon's ITSM software auto-routes tickets, builds knowledge bases, and enforces SLA escalations - an AI-powered ITSM tool that speeds up IT service delivery.`,
          og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
          og_type: 'website',
          og_image_type: 'image/webp',
          og_image_alt: 'Infraon ITSM dashboard showing incident management and SLA tracking',

          site_name: 'Infraon',
          twitter_title: 'ITSM Tool | AI-Enabled ITSM Software by Infraon',
          twitter_desc: `Infraon's ITSM software auto-routes tickets, builds knowledge bases, and enforces SLA escalations - an AI-powered ITSM tool that speeds up IT service delivery.`,
          twitter_image: 'https://infraon.io/assets/img/feature-imgs/itsm/itsm-dashboard.png',
          twitter_image_alt: 'Infraon ITSM dashboard showing ticket analytics and SLA tracking',
          href_langs: [
            { lang: 'en-MY', href: 'https://infraon.io/my/itsm-tool' },
            { lang: 'en-ID', href: 'https://infraon.io/id/itsm-tool' },
            { lang: 'id-ID', href: 'https://infraon.io/id/itsm-tool/bahasa' },
            { lang: 'en', href: 'https://infraon.io/itsm-tool' },
            { lang: 'x-default', href: 'https://infraon.io/itsm-tool' },
          ],
          json_ld: {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Infraon',
            url: 'https://infraon.io/',
          },
          submenu: ITSM_SUBMENUS,
          footerBanner: {
            bgImage: '/assets/img/bgs/assets/cta-banner.svg',
            titleHtml: `<span class="emphasis">Why Use Multiple ITSM Tools? </span> Deploy One and Be Done!`,
            descHtml: ``,
            pointers: [
              'No credit card details required',
              'Custom walkthrough for your use case',
              ' Live savings calculator',
            ],
            color: 'itsm',
            cta: 'Start Your Free Trial',
            link: 'itsm-tool',
          },
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./products/infraon-itsm/sass/sass-infraon-itsm').then(
                (m) => m.SassInfraonItsm,
              ),
          },
        ],
      },
      {
        path: 'it-ops-management',
        pathMatch: 'full',
        canActivate: [EditionGuard],
        loadComponent: () =>
          import('./products/infraon-itops/sass/homepage').then((m) => m.Homepage),
        data: {
          variant: 'saas',
          hasSubmenu: false,
          hasDualContext: true,
          meta_title: 'ITOps Tool | AI-Powered IT Operations Management Tool',
          meta_desc:
            'Simplify IT with Infraon Infinity - an AI-driven ITOps tool and IT operations management tool that automates monitoring, boosts uptime, and streamlines workflows.',
          canonical_url: '/it-ops-management',
          sitemapKey: 'Infinity-Sass',
          colorScheme: 'infinity-theme',

          og_image: 'https://infraon.io/assets/img/feature-imgs/nms/ims-dashboard.png',
          og_type: 'website',
          og_url: 'https://infraon.io/it-ops-management',
          site_name: 'Infraon',
          og_image_type: 'image/png',
          og_image_alt: 'Infraon Infinity unified IT operations management platform dashboard',

          twitter_title: 'ITOps Tool | AI-Powered IT Operations Management Tool',
          twitter_desc:
            'Simplify IT with Infraon Infinity - an AI-driven ITOps tool and IT operations management tool that automates monitoring, boosts uptime, and streamlines workflows.',
          twitter_image: 'https://infraon.io/assets/img/feature-imgs/nms/ims-dashboard.png',
          twitter_image_alt: 'Infraon Infinity unified IT operations management platform dashboard',

          json_ld: {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Infraon',
            url: 'https://infraon.io/',
          },

          breadcrumb: 'IT Operation Management',
          breadcrumb_color: 'white',
          footerBanner: {
            bgImage: '/assets/img/feature-imgs/homepage/footer-cta.svg',
            titleHtml: `<span class="emphasis">Simplify, Accelerate & Unify Asset & Service Management </span>`,
            descHtml: ``,
            pointers: [
              'No credit card details required',
              'Custom walkthrough for your use case',
              'Live savings calculator',
            ],
            color: 'infinity',
            cta: 'Take Your Free Trial',
          },
        },
      },

      // Country-specific landing pages (Malaysia first; Indonesia etc. follow the same pattern as sibling paths)
      {
        path: 'my',
        data: {
          breadcrumb: 'Malaysia',
          canonical_url: '/my',

          meta_title: 'GenAI-Based ITSM Software for Malaysian IT Teams | Infraon',
          meta_desc: `Infraon ITSM built for Malaysia-centric service issues - real-time IT governance, multi-entity service desks, and regional data protection.`,

          og_title: 'GenAI-Based ITSM Software for Malaysian IT Teams | Infraon',
          og_url: 'https://infraon.io/my',
          og_desc: `Infraon ITSM built for Malaysia-centric service issues - real-time IT governance, multi-entity service desks, and regional data protection.`,
          og_image: 'https://infraon.io/assets/img/feature-imgs/homepage/hero-dashboard.png',
          og_image_type: 'image/png',
          og_image_alt: 'Infraon ITSM dashboard for Malaysian IT teams',

          twitter_title: 'GenAI-Based ITSM Software for Malaysian IT Teams | Infraon',
          twitter_desc: `Infraon ITSM built for Malaysia-centric service issues - real-time IT governance, multi-entity service desks, and regional data protection.`,
          twitter_image: 'https://infraon.io/assets/img/feature-imgs/homepage/hero-dashboard.png',
          twitter_image_alt: 'Infraon ITSM dashboard for Malaysian IT teams',
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/country-pages/my-index/my-index').then((m) => m.MyIndexComponent),
          },
          {
            path: 'itsm-tool',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/country-pages/itsm/country-itsm-landing').then(
                (m) => m.CountryItsmLanding,
              ),
            data: {
              country: 'my',
              breadcrumb: 'ITSM Tool',
              sitemapKey: 'ITSM',
              productName: 'Infraon ITSM',
              colorScheme: 'itsm-theme',
              hideGlobalFooter: true,
              hideGlobalNavbar: true,
              canonical_url: '/my/itsm-tool',

              meta_title: 'ITSM Software Malaysia - GenAI Service Desk | Infraon',
              meta_desc: `Infraon ITSM unifies service desk, asset management and monitoring in one platform. ITIL 4 and PinkVERIFY certified. Go live in 4 weeks. Book a demo.`,

              og_title: 'ITSM Software Malaysia - GenAI Service Desk | Infraon',
              og_url: 'https://infraon.io/my/itsm-tool',
              og_desc: `Infraon ITSM unifies service desk, asset management and monitoring in one platform. ITIL 4 and PinkVERIFY certified. Go live in 4 weeks. Book a demo.`,
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/itsm-dashboard.png',
              og_image_type: 'image/png',
              og_image_alt: 'Infraon ITSM dashboard showing incident management and SLA tracking',
              og_locale: 'en_MY',

              twitter_title: 'ITSM Software Malaysia - GenAI Service Desk | Infraon',
              twitter_desc: `Infraon ITSM unifies service desk, asset management and monitoring in one platform. ITIL 4 and PinkVERIFY certified. Go live in 4 weeks. Book a demo.`,
              twitter_image: 'https://infraon.io/assets/img/feature-imgs/itsm/itsm-dashboard.png',
              twitter_image_alt: 'Infraon ITSM dashboard showing ticket analytics and SLA tracking',

              href_langs: [
                { lang: 'en-MY', href: 'https://infraon.io/my/itsm-tool' },
                { lang: 'en-ID', href: 'https://infraon.io/id/itsm-tool' },
                { lang: 'id-ID', href: 'https://infraon.io/id/itsm-tool/bahasa' },
                { lang: 'en', href: 'https://infraon.io/itsm-tool' },
                { lang: 'x-default', href: 'https://infraon.io/itsm-tool' },
              ],
            },
          },
        ],
      },

      {
        path: 'id',
        data: {
          breadcrumb: 'Indonesia',
          canonical_url: '/id',

          meta_title: 'GenAI-Based ITSM Software for Indonesian IT Teams | Infraon',
          meta_desc: `Infraon ITSM built for Indonesia-centric service issues - real-time IT governance, multi-entity service desks, and regional data protection.`,

          og_title: 'GenAI-Based ITSM Software for Indonesian IT Teams | Infraon',
          og_url: 'https://infraon.io/id',
          og_desc: `Infraon ITSM built for Indonesia-centric service issues - real-time IT governance, multi-entity service desks, and regional data protection.`,
          og_image: 'https://infraon.io/assets/img/feature-imgs/homepage/hero-dashboard.png',
          og_image_type: 'image/png',
          og_image_alt: 'Infraon ITSM dashboard for Indonesian IT teams',

          twitter_title: 'GenAI-Based ITSM Software for Indonesian IT Teams | Infraon',
          twitter_desc: `Infraon ITSM built for Indonesia-centric service issues - real-time IT governance, multi-entity service desks, and regional data protection.`,
          twitter_image: 'https://infraon.io/assets/img/feature-imgs/homepage/hero-dashboard.png',
          twitter_image_alt: 'Infraon ITSM dashboard for Indonesian IT teams',
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/country-pages/id-index/id-index').then((m) => m.IdIndexComponent),
          },
          {
            path: 'itsm-tool',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/country-pages/itsm/country-itsm-landing').then(
                (m) => m.CountryItsmLanding,
              ),
            data: {
              country: 'id',
              breadcrumb: 'ITSM Tool',
              sitemapKey: 'ITSM',
              productName: 'Infraon ITSM',
              colorScheme: 'itsm-theme',
              hideGlobalFooter: true,
              hideGlobalNavbar: true,
              canonical_url: '/id/itsm-tool',

              meta_title: 'Software ITSM Indonesia - GenAI Service Desk | Infraon',
              meta_desc: `Infraon ITSM brings service desk, assets and monitoring into one platform. Deploy on-premises or cloud. ITIL 4 certified. Go live in 4 weeks. Book a demo.`,

              og_title: 'Software ITSM Indonesia - GenAI Service Desk | Infraon',
              og_url: 'https://infraon.io/id/itsm-tool',
              og_desc: `Infraon ITSM brings service desk, assets and monitoring into one platform. Deploy on-premises or cloud. ITIL 4 certified. Go live in 4 weeks. Book a demo.`,
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/itsm-dashboard.png',
              og_image_type: 'image/png',
              og_image_alt: 'Infraon ITSM dashboard showing incident management and SLA tracking',
              og_locale: 'en_ID',

              twitter_title: 'Software ITSM Indonesia - GenAI Service Desk | Infraon',
              twitter_desc: `Infraon ITSM brings service desk, assets and monitoring into one platform. Deploy on-premises or cloud. ITIL 4 certified. Go live in 4 weeks. Book a demo.`,
              twitter_image: 'https://infraon.io/assets/img/feature-imgs/itsm/itsm-dashboard.png',
              twitter_image_alt:
                'Infraon ITSM dashboard showing incident management and SLA tracking',

              href_langs: [
                { lang: 'en-MY', href: 'https://infraon.io/my/itsm-tool' },
                { lang: 'en-ID', href: 'https://infraon.io/id/itsm-tool' },
                { lang: 'id-ID', href: 'https://infraon.io/id/itsm-tool/bahasa' },
                { lang: 'en', href: 'https://infraon.io/itsm-tool' },
                { lang: 'x-default', href: 'https://infraon.io/itsm-tool' },
              ],
            },
          },
          {
            path: 'itsm-tool/bahasa',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/country-pages/itsm/country-itsm-landing').then(
                (m) => m.CountryItsmLanding,
              ),
            data: {
              country: 'id',
              locale: 'id',
              breadcrumb: 'Alat ITSM',
              sitemapKey: 'ITSM',
              productName: 'Infraon ITSM',
              colorScheme: 'itsm-theme',
              hideGlobalFooter: true,
              hideGlobalNavbar: true,
              canonical_url: '/id/itsm-tool/bahasa',
              lang_code: 'id',

              meta_title: 'Software ITSM Indonesia | Aplikasi Service Desk | Infraon',
              meta_desc: `Infraon ITSM menyatukan service desk, aset, dan monitoring dalam satu platform. Deploy on-premises atau cloud. Tersertifikasi ITIL 4. Go-live dalam 4 minggu. Pesan demo.`,

              og_title: 'Software ITSM Indonesia | Aplikasi Service Desk | Infraon',
              og_url: 'https://infraon.io/id/itsm-tool/bahasa',
              og_desc: `Infraon ITSM menyatukan service desk, aset, dan monitoring dalam satu platform. Deploy on-premises atau cloud. Tersertifikasi ITIL 4. Go-live dalam 4 minggu. Pesan demo.`,
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/itsm-dashboard.png',
              og_image_type: 'image/png',
              og_locale: 'id_ID',
              og_image_alt:
                'Dashboard Infraon ITSM menampilkan manajemen insiden dan pelacakan SLA',

              twitter_title: 'Software ITSM Indonesia - Service Desk GenAI | Infraon',
              twitter_desc: `Infraon ITSM menyatukan service desk, aset, dan monitoring dalam satu platform. Deploy on-premises atau cloud. Tersertifikasi ITIL 4. Go-live dalam 4 minggu. Pesan demo.`,
              twitter_image: 'https://infraon.io/assets/img/feature-imgs/itsm/itsm-dashboard.png',
              twitter_image_alt:
                'Dashboard Infraon ITSM menampilkan manajemen insiden dan pelacakan SLA',
              href_langs: [
                { lang: 'en-MY', href: 'https://infraon.io/my/itsm-tool' },
                { lang: 'en-ID', href: 'https://infraon.io/id/itsm-tool' },
                { lang: 'id-ID', href: 'https://infraon.io/id/itsm-tool/bahasa' },
                { lang: 'en', href: 'https://infraon.io/itsm-tool' },
                { lang: 'x-default', href: 'https://infraon.io/itsm-tool' },
              ],
            },
          },
        ],
      },

      {
        path: '',
        // canActivateChild: [EditionGuard],
        children: [...PRODUCT_CHILD_ROUTES],
      },

      // itim/ims
      {
        path: 'itim-ims',
        data: {
          hasSubmenu: true,
          hasDualContext: false,
          breadcrumb: 'IT Infrastructure Management',
          breadcrumb_color: 'IT Infrastructure Management',
          colorScheme: 'nms-theme',
          sitemapKey: 'IMS',
          productName: 'Infraon IMS/ITIM',
          productIcon: '/assets/img/logos/products/nms-element.svg',
          meta_title: 'IT Infrastructure Management Software | Infraon',
          meta_desc:
            'Manage your IT with real-time infrastructure monitoring, device management, automated alerts, and centralized visibility from one platform.',
          canonical_url: '/itim-ims',

          og_title: 'IT Infrastructure Management Software | Infraon',
          og_url: 'https://infraon.io/itim-ims',
          og_desc:
            'Manage your IT with real-time infrastructure monitoring, device management, automated alerts, and centralized visibility from one platform.',
          og_image: 'https://infraon.io/assets/img/feature-imgs/nms/ims-dashboard.png',
          og_image_type: 'image/png',
          og_image_alt:
            'Infraon IMS dashboard showing real-time network monitoring and infrastructure alerts',

          twitter_title: 'IT Infrastructure Management Software | Infraon',
          twitter_desc:
            'Manage your IT with real-time infrastructure monitoring, device management, automated alerts, and centralized visibility from one platform.',
          twitter_image: 'https://infraon.io/assets/img/feature-imgs/nms/ims-dashboard.png',

          twitter_image_alt:
            'Infraon IMS dashboard showing real-time network monitoring and infrastructure alerts',
          submenu: IMS_SUBMENUS,
          footerBanner: {
            bgImage: '/assets/img/bgs/assets/cta-banner.svg',
            titleHtml: `<span class="emphasis"><em>Stop Drowning in Alert Noise</em> – Start Using Artificial Intelligence for IT Operations</span>`,
            descHtml: `Transform IT operations from reactive firefighting to proactive intelligence. Infraon AIOps tool delivers intelligent alert management, automated root cause analysis, predictive failure detection, and AI-powered remediation.`,
            pointers: [
              'No credit card details required',
              'Custom walkthrough for your use case',
              'Live savings calculator',
            ],
            color: 'nms',
            cta: 'Book a 30-min Demo',
          },
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./products/infraon-ims/infraon-ims').then((m) => m.InfraonIMS),
            data: {
              colorScheme: 'nms-theme',
            },
          },
          {
            path: '',
            loadChildren: () =>
              import('./products/infraon-ims/infraon-ims.routes').then((m) => m.INFRAON_IMS_ROUTES),
          },
        ],
      },

      // aiops
      {
        path: 'ai-ops-tool',
        loadComponent: () =>
          import('./products/infraon-aiops/infraon-aiops').then((m) => m.InfraonAiops),
        data: {
          meta_title: 'AIOps Tool: Artificial Intelligence for IT Operations',
          hasSubmenu: false,
          hasDualContext: false,
          meta_desc:
            'Transform IT operations with artificial intelligence for IT operations. AIOps tool reduces alert noise, predicts failures & automates remediation.',
          canonical_url: '/ai-ops-tool',
          breadcrumb: 'AIOps Tool',
          breadcrumb_color: 'AIOps Tool',
          colorScheme: 'aiops-theme',

          og_title: 'AIOps Tool: Artificial Intelligence for IT Operations',
          og_url: 'https://infraon.io/ai-ops-tool',
          og_desc:
            'Transform IT operations with artificial intelligence for IT operations. AIOps tool reduces alert noise, predicts failures & automates remediation.',
          og_image: 'https://infraon.io/assets/img/feature-imgs/aiops/aiops-dashboard.png',
          og_image_type: 'image/png',
          og_image_alt:
            'Infraon AIOps dashboard showing AI-driven alert correlation and predictive analytics',

          twitterTitle: 'AIOps Tool | AI-Powered IT Operations Management | Infraon',
          twitterDesc:
            'Transform IT operations with artificial intelligence. Infraon AIOps reduces alert noise, predicts failures before they happen, and automates incident remediation.',
          twitterImage: 'https://infraon.io/assets/img/feature-imgs/aiops/aiops-dashboard.png',
          twitterImageAlt:
            'Infraon AIOps dashboard showing AI-driven alert correlation and predictive analytics',

          footerBanner: {
            bgImage: '/assets/img/bgs/assets/cta-banner.svg',
            titleHtml: `<span class="emphasis"><em>Stop Drowning in Alert Noise</em> – Start Using Artificial Intelligence for IT Operations</span>`,
            descHtml: `Transform IT operations from reactive firefighting to proactive intelligence. Infraon AIOps tool delivers intelligent alert management, automated root cause analysis, predictive failure detection, and AI-powered remediation.`,
            pointers: [
              'No credit card details required',
              'Custom walkthrough for your use case',
              'Live savings calculator',
            ],
            color: 'aiops',
            cta: 'Book a 30-min Demo',
          },
        },
      },

      // nccm
      {
        path: 'nccm',
        loadComponent: () =>
          import('./products/infraon-nccm/infraon-nccm').then((m) => m.InfraonNccm),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          breadcrumb: 'Infraon NCCM',
          breadcrumb_color: 'Infraon NCCM',
          colorScheme: 'nccm-theme',
          meta_title: 'Network Configuration and Change Management | Infraon NCCM',
          meta_desc:
            'NCCM software automates network configuration and change management compliance workflows, configuration backup, and vulnerability management for 15,000+ devices.',
          canonical_url: '/nccm',

          og_title: 'Network Configuration and Change Management | Infraon NCCM',
          og_url: 'https://infraon.io/nccm',
          og_desc:
            'NCCM software automates network configuration and change management compliance workflows, configuration backup, and vulnerability management for 15,000+ devices.',
          og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.png',
          og_image_type: 'image/png',
          og_image_alt:
            'Infraon NCCM dashboard showing network configuration compliance and change management',

          twitterTitle: 'Network Configuration & Change Management Software | Infraon NCCM',
          twitterDesc:
            'NCCM software automates network configuration and change management compliance workflows, configuration backup, and vulnerability management for 15,000+ devices.',
          twitterImage: 'https://infraon.io/assets/img/feature-imgs/nccm/nccm-dashboard.png',
          twitterImageAlt:
            'Infraon NCCM dashboard showing network configuration compliance and change management',

          footerBanner: {
            bgImage: '/assets/img/bgs/assets/cta-banner.svg',
            titleHtml: `<span class="emphasis"><em>Stop Drowning in Alert Noise</em> – Start Using Artificial Intelligence for IT Operations</span>`,
            descHtml: `Transform IT operations from reactive firefighting to proactive intelligence. Infraon AIOps tool delivers intelligent alert management, automated root cause analysis, predictive failure detection, and AI-powered remediation.`,
            pointers: [
              'No credit card details required',
              'Custom walkthrough for your use case',
              'Live savings calculator',
            ],
            color: 'nccm',
            cta: 'Book a 30-min Demo',
          },
        },
      },

      // oss
      {
        path: 'infraon-oss',
        loadComponent: () =>
          import('./products/infraon-oss/infraon-oss-new').then((m) => m.InfraonOss),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          breadcrumb: 'Infraon OSS',
          breadcrumb_color: 'Infraon OSS',
          colorScheme: 'oss-theme',
          meta_title: 'Best Customer Centric OSS System | Infraon',
          meta_desc:
            'Discover the key benefits of Infraon’s Customer-Centric Digital OSS System, designed to enhance telecom network performance and reliability. | Infraon',
          canonical_url: '/infraon-oss',

          og_title: 'Operations Support System for Telecom Networks | Infraon OSS',
          og_url: 'https://infraon.io/infraon-oss',
          og_desc:
            'Discover the key benefits of Infraon’s Customer-Centric Digital OSS System, designed to enhance telecom network performance and reliability. | Infraon',
          og_image: 'https://infraon.io/assets/img/feature-imgs/oss/header-bg-oss.svg',
          og_image_type: 'image/svg',
          og_image_alt:
            'Infraon OSS platform for telecom network operations, service assurance and inventory management',

          twitterTitle: 'Telecom OSS | Operations Support System for Networks | Infraon',
          twitterDesc:
            'Discover the key benefits of Infraon’s customer-centric Digital OSS System, designed to enhance telecom network performance, service fulfilment, and reliability.',
          twitterImage: 'https://infraon.io/assets/img/feature-imgs/oss/header-bg-oss.webp',
          twitterImageAlt:
            'Infraon OSS platform for telecom network operations, service assurance and inventory management',

          footerBanner: {
            bgImage: '/assets/img/bgs/oss/new/footer-banner.svg',
            titleHtml: `<span class="emphasis">Ready to Streamline Your OSS Journey <br > With a Single Platform? </span>`,
            descHtml: ``,
            pointers: [],
            color: 'oss',
            cta: 'Talk to An Expert',
          },
        },
      },

      //registration
      {
        path: 'registration',
        loadComponent: () =>
          import('./pages/app-registration/registration').then((m) => m.Registration),
        data: {
          hasSubmenu: false,
          hasDualContext: true,
          // The same route serves both forms: ?type=demo is the demo view.
          breadcrumb: (_data: Data, route: ActivatedRouteSnapshot) =>
            route.queryParamMap.get('type') === 'demo' ? 'Demo' : 'Registration',
          canonical_url: '/registration',
          colorScheme: 'infinity-theme',
          meta_title: 'Product Registration | Saas Solutions | Infraon',
          meta_desc:
            'Register with Infraon to access our tools and services. Start your journey towards efficiency and innovation. Sign up with Infraon now!',

          og_title: 'Product Registration | Saas Solutions | Infraon',
          og_url: 'https://infraon.io/registration',
          og_desc:
            'Register with Infraon to access our tools and services. Start your journey towards efficiency and innovation. Sign up with Infraon now!',
          og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
          og_image_type: 'image/webp',
          og_image_alt:
            'Infraon product registration page with user sign-up form and benefits of registering',
          robot_noindex: 'noindex, follow',
        },
      },

      // free tools
      {
        path: 'free-tools',
        children: [
          // asset depreciaition calculator
          {
            path: 'asset-depreciation-calculator',
            loadChildren: () =>
              import('./pages/free-tools/asset-depreciation-calculator/asset-depreciation-calculator.routes').then(
                (m) => m.DEPRECIATION_CALCULATOR_ROUTES,
              ),
            data: {
              hasSubmenu: false,
              hasDualContext: false,
              breadcrumb: 'Asset Depreciation Calculator',
              breadcrumb_color: 'white',
              colorScheme: '',
              meta_title: 'Asset Depreciation Calculator & Management Solutions | Infraon',
              meta_desc:
                "Calculate asset depreciation with Infraon's tool. Simplify financial planning and asset management. Try our easy-to-use depreciation calculator today!",
              canonical_url: '/free-tools/asset-depreciation-calculator',

              og_title: 'Asset Depreciation Calculator & Management Solutions | Infraon',
              og_url: 'https://infraon.io/free-tools/asset-depreciation-calculator',
              og_desc:
                "Calculate asset depreciation with Infraon's tool. Simplify financial planning and asset management. Try our easy-to-use depreciation calculator today!",
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon Asset Depreciation Calculator showing asset value over time and depreciation schedules',
            },
          },

          // itam saving calculator
          {
            path: 'itam-savings-calculator',
            loadChildren: () =>
              import('./pages/free-tools/itam-saving-calculator/itam-saving-calculator.routes').then(
                (m) => m.ITAM_SAVING_CALCULATOR_ROUTES,
              ),
            data: {
              breadcrumb: 'ITAM Saving Calculator',
              breadcrumb_color: 'white',
              colorScheme: '',
              meta_title:
                'Welcome To ITAM Time and Cost Savings Calculator! | Infraon Assets | Infraon',
              meta_desc: 'Quantify ITAM savings with clear time and cost analysis.',
              canonical_url: '/free-tools/itam-savings-calculator',

              og_title:
                'Welcome To ITAM Time and Cost Savings Calculator! | Infraon Assets | Infraon',
              og_url: 'https://infraon.io/free-tools/itam-savings-calculator',
              og_desc: 'Quantify ITAM savings with clear time and cost analysis.',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon ITAM Savings Calculator showing time and cost savings from IT asset management improvements',
            },
          },

          // service-desk-roi
          {
            path: 'service-desk-roi-calculator',
            loadChildren: () =>
              import('./pages/free-tools/service-desk-roi/service-desk-roi.routes').then(
                (m) => m.ROI_CALCULATOR_ROUTES,
              ),
            data: {
              breadcrumb: 'Service Desk ROI Calculator',
              breadcrumb_color: 'Service Desk ROI Calculator',
              colorScheme: 'irec-theme',
              meta_title: 'Service Desk ROI Calculator | Calculate ITSM ROI',
              meta_desc: `Calculate your service desk ROI with Infraon's free ROI calculator. Estimate cost savings, productivity gains, and the business value of ITSM.`,
              canonical_url: '/free-tools/service-desk-roi-calculator',

              og_title: 'Service Desk ROI Calculator | Calculate ITSM ROI',
              og_url: 'https://infraon.io/free-tools/service-desk-roi-calculator',
              og_desc: `Calculate your service desk ROI with Infraon's free ROI calculator. Estimate cost savings, productivity gains, and the business value of ITSM.`,
              og_image: 'https://infraon.io/free-tools/service-desk-roi-calculator/images/hero.svg',
              og_image_type: 'image/svg',
              og_image_alt:
                'Infraon Service Desk ROI Calculator showing cost savings and efficiency improvements from IT service management',

              twitter_title: 'Service Desk ROI Calculator | Calculate ITSM ROI',
              twitter_desc: `Calculate your service desk ROI with Infraon's free ROI calculator. Estimate cost savings, productivity gains, and the business value of ITSM.`,
              twitter_image:
                'https://infraon.io/free-tools/service-desk-roi-calculator/images/hero.svg',
              twitter_image_alt:
                'Infraon Service Desk ROI Calculator showing cost savings and efficiency improvements from IT service management',
            },
          },

          // incident response calculator
          {
            path: 'incident-response-calculator',
            loadChildren: () =>
              import('./pages/free-tools/incident-response-calculator/incident-response-calculator.routes').then(
                (m) => m.INCIDENT_RESPONSE_CALCULATOR_ROUTES,
              ),
            data: {
              breadcrumb: 'Incident Response Efficiency Calculator',
              breadcrumb_color: 'white',
              colorScheme: 'irec-theme',
              meta_title: 'Incident Response Efficiency Calculator | Measure MTTR & Cost Savings',
              meta_desc:
                'Calculate incident response efficiency by measuring MTTR, resolution speed, and operational impact. Identify gaps and improve IT incident management performance.',
              canonical_url: '/free-tools/incident-response-calculator',

              og_title: 'Incident Response Efficiency Calculator | Measure MTTR & Cost Savings',
              og_url: 'https://infraon.io/free-tools/incident-response-calculator',
              og_desc:
                'Calculate incident response efficiency by measuring MTTR, resolution speed, and operational impact. Identify gaps and improve IT incident management performance.',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon Incident Response Efficiency Calculator showing MTTR analysis and incident resolution performance',
            },
          },

          // downtime cost estimator
          {
            path: 'downtime-cost-estimator',
            loadChildren: () =>
              import('./pages/free-tools/downtime-calculator/downtime-calculator.routes').then(
                (m) => m.DOWNTIME_CALCULATOR_ROUTES,
              ),
            data: {
              breadcrumb: 'Downtime Cost Estimator',
              breadcrumb_color: 'Downtime Cost Estimator',
              colorScheme: 'irec-theme',
              meta_title: 'Downtime Cost Estimator',
              meta_desc: 'Downtime Cost Estimator',
              canonical_url: '/free-tools/downtime-cost-estimator',

              og_title: 'IT Asset Management Software | Save $400K+',
              og_url: 'https://infraon.io/free-tools/downtime-cost-estimator',
              og_desc:
                'AI-powered IT asset management software that eliminates ghost assets, automates inventory, and improves license compliance. Cut IT costs by 30% with fast deployment.',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon Downtime Cost Estimator showing financial impact of IT downtime and potential savings from improved reliability',
            },
          },

          // Ticket Backlog Impact Calculator
          {
            path: 'itsm-ticket-backlog-impact-calculator',
            loadComponent: () =>
              import('./pages/free-tools/backlog-calculator/backlog-calculator-page').then(
                (m) => m.BacklogCalculatorPageComponent,
              ),
            data: {
              breadcrumb: 'ITSM Ticket Backlog Impact Calculator',
              breadcrumb_color: 'infinity',
              colorScheme: 'irec-theme',
              meta_title:
                'ITSM Ticket Backlog Impact Calculator | Measure Cost, Productivity & SLA Risk',
              meta_desc:
                'Calculate the real impact of your IT support backlog. Estimate productivity loss, cost impact, backlog growth, and SLA risk with our ITSM Ticket Backlog Impact Calculator.',
              canonical_url: '/free-tools/itsm-ticket-backlog-impact-calculator',

              og_title:
                'ITSM Ticket Backlog Impact Calculator | Measure Cost, Productivity & SLA Risk',
              og_url: 'https://infraon.io/free-tools/itsm-ticket-backlog-impact-calculator',
              og_desc:
                'Calculate the real impact of your IT support backlog. Estimate productivity loss, cost impact, backlog growth, and SLA risk with our ITSM Ticket Backlog Impact Calculator.',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon ITSM Ticket Backlog Impact Calculator showing backlog growth projections and cost impact analysis',
            },
          },

          // MTTR Impact Analyzer
          {
            path: 'mttr-impact-analyzer',
            loadComponent: () =>
              import('./pages/free-tools/fault-resolution-calculator/fault-resolution-calculator').then(
                (m) => m.FaultResolutionCalculatorComponent,
              ),
            data: {
              breadcrumb: 'MTTR Impact Analyzer',
              breadcrumb_color: 'infinity',
              colorScheme: 'irec-theme',
              meta_title: 'MTTR Impact Analyzer | SLA, MTTR & Churn Impact Tool',
              meta_desc:
                'Calculate SLA breaches, MTTR impact, customer churn risk, and revenue loss from delayed fault resolution.',
              canonical_url: '/free-tools/mttr-impact-analyzer',

              og_title: 'MTTR Impact Analyzer | SLA, MTTR & Churn Impact Tool',
              og_url: 'https://infraon.io/free-tools/mttr-impact-analyzer',
              og_desc:
                'Calculate SLA breaches, MTTR impact, customer churn risk, and revenue loss from delayed fault resolution.',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon MTTR Impact Analyzer shows resolution time projections and cost impact analysis',
            },
          },

          // AlertNoise IQ
          {
            path: 'alert-fatigue-calculator',
            loadComponent: () =>
              import('./pages/free-tools/alertnoise-iq/alertnoise-iq').then(
                (m) => m.AlertNoiseIqComponent,
              ),
            data: {
              breadcrumb: 'MTTR Impact Analyzer',
              breadcrumb_color: 'infinity',
              colorScheme: 'irec-theme',
              meta_title:
                'Alert Fatigue Calculator: Measure Time Wasted on False Positives | Infraon',
              meta_desc:
                "Engineers overwhelmed by alerts? Calculate your team's hidden alert fatigue costs and discover how much engineering time you can reclaim with AIOps event correlation.",
              canonical_url: '/free-tools/alert-fatigue-calculator',

              og_title:
                'Alert Fatigue Calculator: Measure Time Wasted on False Positives | Infraon',
              og_url: 'https://infraon.io/free-tools/alert-fatigue-calculator',
              og_desc:
                "Engineers overwhelmed by alerts? Calculate your team's hidden alert fatigue costs and discover how much engineering time you can reclaim with AIOps event correlation.",
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon MTTR Impact Analyzer shows resolution time projections and cost impact analysis',
            },
          },
        ],
      },

      // contact us
      {
        path: 'contact-us',
        loadComponent: () => import('./pages/contact-us/contact-us').then((m) => m.ContactUs),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          colorScheme: 'contact-theme',
          breadcrumb: 'Contact us',
          breadcrumb_color: 'Contact us',
          meta_title: 'Contact Us | Get in Touch with Infraon Team',
          meta_desc:
            'Get in touch with Infraon for expert ITSM, ITOM, and automation solutions. Reach out to us for demos, inquiries, or support. Let’s transform your business together!',
          canonical_url: '/contact-us',

          og_title: 'Contact Us | Get in Touch with Infraon Team',
          og_url: 'https://infraon.io/contact-us',
          og_desc:
            'Get in touch with Infraon for expert ITSM, ITOM, and automation solutions. Reach out to us for demos, inquiries, or support. Let’s transform your business together!',
          og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
          og_image_type: 'image/webp',
          og_image_alt: 'Infraon contact us page with contact form and company information',
        },
      },

      // resources
      {
        path: 'resources',
        loadComponent: () => import('./pages/resources/resources').then((m) => m.Resources),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          colorScheme: 'resources-theme',
          meta_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
          meta_desc:
            'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
          canonical_url: '/resources',

          og_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
          og_url: 'https://infraon.io/resources',
          og_desc:
            'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
          og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
          og_image_type: 'image/webp',
          og_image_alt:
            'Infraon resource hub with brochures, case studies, whitepapers and datasheets',

          twitterTitle: 'IT Operations Resources | Brochures, Case Studies & More | Infraon',
          twitterDesc: `Access Infraon's full library of resources - brochures, case studies, datasheets, whitepapers, e-books, and videos covering ITSM, ITAM, network monitoring, and AIOps.`,
          twitterImage: 'https://infraon.io/assets/img/feature-imgs/resources/resources-hub.webp',
          twitterImageAlt:
            'Infraon resource hub with brochures, case studies, whitepapers and datasheets',
        },

        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/resources/featured-resources').then((m) => m.FeaturedResources),
            data: {
              meta_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              meta_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              canonical_url: '/resources',

              og_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              og_url: 'https://infraon.io/resources',
              og_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon resource hub with brochures, case studies, whitepapers and datasheets',
            },
          },
          {
            path: 'topic/:topic',
            canActivate: [resourceTopicGuard],
            loadComponent: () =>
              import('./pages/resources/resource-topics').then((m) => m.ResourceTopics),
            data: {
              meta_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              meta_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              canonical_url: '/resources',

              og_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              og_url: 'https://infraon.io/resources',
              og_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon resource hub with brochures, case studies, whitepapers and datasheets',
            },
          },
          {
            path: ':type/:slug',
            canActivate: [resourceDetailGuard],
            loadComponent: () =>
              import('./pages/resources/resource-detail').then((m) => m.ResourceDetail),
            data: {
              meta_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              meta_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              canonical_url: '/resources',

              og_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              og_url: 'https://infraon.io/resources',
              og_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon resource detail page showing in-depth information about a specific resource such as a case study or datasheet',
            },
          },
          {
            path: 'search',
            loadComponent: () =>
              import('./pages/resources/search-resources').then((m) => m.SearchResources),
            data: {
              meta_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              meta_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              canonical_url: '/resources',

              og_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              og_url: 'https://infraon.io/resources',
              og_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon resource search page with filters and search results for brochures, case studies, whitepapers and datasheets',
            },
          },
          {
            path: ':type',
            canActivate: [resourceTypeGuard],
            loadComponent: () =>
              import('./pages/resources/resources-types').then((m) => m.ResourcesTypes),
            data: {
              meta_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              meta_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              canonical_url: '/resources',

              og_title: 'Infraon Resources: Brochures, Case Studies, Datasheets, Videos & More',
              og_url: 'https://infraon.io/resources',
              og_desc:
                'Access Infraon’s resources for key insights. Enhance your knowledge and optimize operations. Discover valuable resources with us today',
              og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
              og_image_type: 'image/webp',
              og_image_alt:
                'Infraon resource hub with brochures, case studies, whitepapers and datasheets categorized by resource type',
            },
          },
        ],
      },

      //company
      {
        path: 'about-us',
        loadComponent: () => import('./pages/company/about/about').then((m) => m.About),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          colorScheme: 'resources-theme',
          breadcrumb: 'About Us',
          breadcrumb_color: 'white',
          meta_title: 'About Us | Infraon - Innovating IT Solutions',
          meta_desc:
            'Discover Infraon: A leader in ITSM, ITOM & asset management. Powering digital transformation with AI, automation & cloud solutions. Get started today!',
          canonical_url: '/about-us',

          og_title: 'About Us | Infraon - Innovating IT Solutions',
          og_url: 'https://infraon.io/about-us',
          og_desc:
            'Discover Infraon: A leader in ITSM, ITOM & asset management. Powering digital transformation with AI, automation & cloud solutions. Get started today!',
          og_image: 'https://infraon.io/assets/img/about/our-origin-story.webp',
          og_image_type: 'image/webp',
          og_image_alt:
            'Infraon team and company origin story - innovating IT operations management',

          twitterTitle: 'About Infraon | Innovating IT Solutions with AI & Automation',
          twitterDesc:
            'Discover Infraon: a leader in ITSM, ITOM & asset management. Powering digital transformation with AI, automation & cloud solutions across 300+ enterprise deployments.',
          twitterImage: 'https://infraon.io/assets/img/about/our-origin-story.webp',
          twitterImageAlt:
            'Infraon team and company origin story - innovating IT operations management',
        },
      },

      //sitemnap
      {
        path: 'sitemap',
        loadComponent: () => import('./pages/sitemap/sitemap').then((m) => m.SitemapComponent),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          canonical_url: '/sitemap',
          colorScheme: 'resources-theme',
          breadcrumb: 'Sitemap',
          breadcrumb_color: 'Sitemap',
          meta_title: 'Sitemap | Infraon - Easy Navigation to Our Services',
          meta_desc:
            'Navigate Infraon’s website with ease. Find the tools and resources you need. Explore our sitemap to learn more about what we offer!',

          og_title: 'Sitemap | Infraon - Easy Navigation to Our Services',
          og_url: 'https://infraon.io/sitemap',
          og_desc:
            'Navigate Infraon’s website with ease. Find the tools and resources you need. Explore our sitemap to learn more about what we offer!',
          og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
          og_image_type: 'image/webp',
          og_image_alt:
            'Infraon sitemap page showing website structure and navigation for IT operations management solutions',
        },
      },

      //events
      {
        path: 'events',
        loadComponent: () => import('./pages/company/events/events').then((m) => m.Events),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          canonical_url: '/events',
          colorScheme: 'resources-theme',
          breadcrumb: 'Events',
          meta_title: 'Infraon Events | Stay Updated with Our Latest Happenings',
          meta_desc:
            'Stay informed about Infraon’s events. Join us for insights and networking opportunities. Register for our upcoming events today!',

          og_title: 'Infraon Events | Stay Updated with Our Latest Happenings',
          og_url: 'https://infraon.io/events',
          og_desc:
            'Stay informed about Infraon’s events. Join us for insights and networking opportunities. Register for our upcoming events today!',
          og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
          og_image_type: 'image/webp',
          og_image_alt:
            'Infraon team at industry technology events showcasing IT operations solutions',

          twitterTitle: 'Infraon Events | Stay Updated with Our Latest Happenings',
          twitterDesc:
            'Stay informed about Infraon’s events. Join us for insights and networking opportunities. Register for our upcoming events today!',
          twitterImage: 'https://infraon.io/assets/img/events/hero/events-hero.webp',
          twitterImageAlt:
            'Infraon team at industry technology events showcasing IT operations solutions',
        },
      },

      //our-partners
      {
        path: 'our-partners',
        loadComponent: () =>
          import('./pages/company/our-partners/our-partners').then((m) => m.OurPartners),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          canonical_url: '/our-partners',
          colorScheme: 'resources-theme',
          breadcrumb: 'Our Partners',
          breadcrumb_color: 'white',
          meta_title: 'Our Partners | Infraon Product Suite',
          meta_desc:
            'Meet trusted partners of Infraon. Collaborate with industry leaders to enhance your operations. Learn more about our expert partners now!',

          og_title: 'Our Partners | Infraon Product Suite',
          og_url: 'https://infraon.io/our-partners',
          og_desc:
            'Meet trusted partners of Infraon. Collaborate with industry leaders to enhance your operations. Learn more about our expert partners now!',
          og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
          og_image_type: 'image/webp',
          og_image_alt:
            'Infraon resource hub with brochures, case studies, whitepapers and datasheets',

          twitterTitle: 'Our Partners | Global IT Solutions Partner Network | Infraon',
          twitterDesc: `Explore Infraon's global partner ecosystem across Asia, the Middle East, and beyond. Trusted by leading system integrators and managed service providers worldwide.`,
          twitterImage: 'https://infraon.io/assets/img/logos/website/infraon-og.png',
          twitterImageAlt: 'Infraon global partner network spanning Asia and Middle East regions',
        },
      },

      //partner-program
      {
        path: 'partner-program',
        loadComponent: () =>
          import('./pages/company/partner-program/partner-program').then((m) => m.PartnerProgram),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          canonical_url: '/partner-program',
          colorScheme: 'resources-theme',
          breadcrumb: 'Partner Program',
          breadcrumb_color: 'white',
          meta_title: 'Partner Program | Infraon',
          meta_desc:
            "IT Asset Lifecycle Management Software: Infraon Assets is a powerful Software, Hardware, and Fixed asset management tool that helps you track, manage and control all of your enterprise's IT assets efficiently. Download a free version now!",

          og_title: 'Partner Program | Infraon',
          og_url: 'https://infraon.io/partner-program',
          og_desc:
            "IT Asset Lifecycle Management Software: Infraon Assets is a powerful Software, Hardware, and Fixed asset management tool that helps you track, manage and control all of your enterprise's IT assets efficiently. Download a free version now!",
          og_image: 'https://infraon.io/assets/img/feature-imgs/itsm/hero-feature.webp',
          og_image_type: 'image/webp',
          og_image_alt:
            'Infraon partner program tiers and benefits for resellers and technology partners',

          twitterTitle: 'Partner Program | Join the Infraon Partner Ecosystem',
          twitterDesc: `IT Asset Lifecycle Management Software: Infraon Assets is a powerful Software, Hardware, and Fixed asset management tool that helps you track, manage and control all of your enterprise's IT assets efficiently. Download a free version now!`,
          twitterImage:
            'https://infraon.io/assets/img/partner-program/why-become/why-become-partner.png',
          twitterImageAlt:
            'Infraon partner program tiers and benefits for resellers and technology partners',
        },
      },

      {
        path: 'privacy-policy',
        loadComponent: () =>
          import('./pages/privacy-policy/privacy-policy').then((m) => m.PrivacyPolicy),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          breadcrumb: 'Privacy Policy',
          colorScheme: 'nccm-theme',
          meta_title: 'Privacy Policy | Infraon',
          meta_desc: `Read Infraon's Privacy Policy to understand how we collect, use, protect, and manage your personal information and website data.`,
          canonical_url: '/privacy-policy',

          og_title: 'Privacy Policy | Infraon',
          og_url: 'https://infraon.io/privacy-policy',
          og_desc: `Read Infraon's Privacy Policy to understand how we collect, use, protect, and manage your personal information and website data.`,
          og_image: '',
          og_image_type: 'image/png',
          og_image_alt: '',
        },
      },
      {
        path: 'terms-of-use',
        loadComponent: () => import('./pages/terms-of-use/terms-of-use').then((m) => m.TermsOfUse),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          breadcrumb: 'Terms of Use',
          colorScheme: 'nccm-theme',
          meta_title: 'Terms of Use | Infraon',
          meta_desc: `Review Infraon's Terms of Use covering website access, acceptable use, intellectual property, user responsibilities, and applicable terms.`,
          canonical_url: '/terms-of-use',

          og_title: 'Terms of Use | Infraon',
          og_url: 'https://infraon.io/terms-of-use',
          og_desc: `Review Infraon's Terms of Use covering website access, acceptable use, intellectual property, user responsibilities, and applicable terms.`,
          og_image: '',
          og_image_type: 'image/png',
          og_image_alt: '',
        },
      },
      {
        path: 'psa',
        loadComponent: () => import('./pages/psa/psa').then((m) => m.Psa),
        data: {
          hasSubmenu: false,
          hasDualContext: false,
          breadcrumb: 'Infraon PSA',
          colorScheme: '',
          meta_title: 'Simplify professional service automation with Infraon Infinity | Infraon',
          meta_desc:
            'Overcome your professional service automation challenges with a no-code,full-stack IT operations management platform. Get on-demand scalability, rapid deployment, and affordable licensing costs.',
          canonical_url: '/psa',

          og_title: 'Simplify professional service automation with Infraon Infinity | Infraon',
          og_url: 'https://infraon.io/psa',
          og_desc:
            'Overcome your professional service automation challenges with a no-code,full-stack IT operations management platform. Get on-demand scalability, rapid deployment, and affordable licensing costs.',
          og_image: 'https://infraon.io/assets/img/psa/psa-header-bg.webp',
          og_image_type: 'image/webp',
          og_image_alt: 'All-in-one suite for enterprises',

          twitterTitle: 'Professional Service Automation | Infraon Infinity PSA',
          twitterDesc:
            'Overcome professional service automation challenges with a no-code, full-stack IT operations management platform. On-demand scalability, rapid deployment, affordable licensing.',
          twitterImage: 'https://infraon.io/assets/img/feature-imgs/infinity/feature.webp',
          twitterImageAlt:
            'Infraon Infinity PSA platform for professional service automation and IT operations',

          footerBanner: {
            bgImage: '/assets/img/bgs/assets/cta-banner.svg',
            titleHtml: `<span class="emphasis">Use Infraon Infinity to do it all - manage </span> multiple clients on one dashboard`,
            descHtml: ``,
            pointers: [
              'No credit card details required',
              'Start your free trial today',
              'Live savings calculator',
            ],
            color: 'infinity',
            cta: 'Take Your Free Trial',
          },
        },
      },
    ],
  },
  {
    path: '**',
    loadComponent: () => import('./pages/not-found/not-found').then((m) => m.NotFoundComponent),
    data: {
      hasSubmenu: false,
      hasDualContext: false,
      meta_title: 'Page Not Found | Infraon',
      meta_desc:
        "The page you're looking for isn't available. Return to Infraon's homepage to explore our AI-powered ITSM, ITAM, NCCM, and observability solutions, or check out our blog for the latest IT operations insights.",
      og_title: 'Page Not Found | Infraon',
      og_desc:
        "The page you're looking for isn't available. Return to Infraon's homepage to explore our AI-powered ITSM, ITAM, NCCM, and observability solutions, or check out our blog for the latest IT operations insights.",
      og_image: 'https://infraon.io/assets/img/psa/psa-header-bg.webp',
      og_image_type: 'image/webp',
      og_image_alt: 'All-in-one suite for enterprises',

      twitterTitle: 'Professional Service Automation | Infraon Infinity PSA',
      twitterDesc:
        'Overcome professional service automation challenges with a no-code, full-stack IT operations management platform. On-demand scalability, rapid deployment, affordable licensing.',
      twitterImage: 'https://infraon.io/assets/img/feature-imgs/infinity/feature.webp',
      twitterImageAlt:
        'Infraon Infinity PSA platform for professional service automation and IT operations',
    },
  },
];
